import { Button } from "@svea/design-system-components-react";
import {
	type AnyQuestion,
	Expandable,
	formatSsn,
	getBoName,
	getQuestionnaireInitialValues,
	Heading,
	type Nullable,
	Questionnaire,
	type QuestionnaireFormValues,
	Stack,
} from "@sveabank/cc-common";
import { useState } from "react";
import Card from "../../../../components/card";
import { usePageContext } from "../../../../hooks/context";
import { useKyc } from "../../hooks";

type Props = {
	onSubmit: (
		questions: Array<AnyQuestion>,
		values: QuestionnaireFormValues,
		beneficialOwnerQuestionKey: string,
	) => Promise<void>;
};

const BeneficialOwnerQuestions = ({ onSubmit }: Props) => {
	const {
		boQuestionStepState,
		beneficialOwnership: { beneficialOwners, foreignBeneficialOwners },
		allCountries,
		applicationDetails: { applicant },
		isOrgEf,
	} = usePageContext(useKyc);

	const getName = (id: string) =>
		isOrgEf
			? `${applicant.firstName || ""} ${applicant.lastName || ""}`
			: getBoName(beneficialOwners, foreignBeneficialOwners, id);

	const [activeAccordion, setActiveAccordion] = useState<Nullable<string>>(null);

	const onEditClick = (nationalId: string) => () => {
		setActiveAccordion((prev) => (prev === nationalId ? null : nationalId));
	};

	const onSubmitClick =
		(questions: Array<AnyQuestion>, beneficialOwnerQuestionKey: string) =>
		async (values: QuestionnaireFormValues) => {
			await onSubmit(questions, values, beneficialOwnerQuestionKey);
			setActiveAccordion(null);
		};

	return (
		<Stack gap={8}>
			{boQuestionStepState.map(
				({
					questions,
					dateOfBirth,
					beneficialOwnerQuestionKeyType,
					beneficialOwnerQuestionKey,
					completed,
				}) => {
					const name = getName(beneficialOwnerQuestionKey);

					const date =
						beneficialOwnerQuestionKeyType === "foreignPersonKey"
							? dateOfBirth || ""
							: formatSsn(beneficialOwnerQuestionKey);

					const isExpanded = activeAccordion === beneficialOwnerQuestionKey || !completed;
					return (
						<Card classNames="bg-background-secondary" key={beneficialOwnerQuestionKey} fullWidth>
							<Stack gap={2} component="article">
								<Stack direction="row" gap={8} align="center">
									<Heading.H4 size="base">{`${name} ${date}`}</Heading.H4>
									{!isExpanded && (
										<Button
											variant="icon"
											onPress={onEditClick(beneficialOwnerQuestionKey)}
											icon="pencil"
											aria-label="Redigera"
										/>
									)}
								</Stack>
								<Expandable isExpanded={isExpanded}>
									<Questionnaire
										onSubmit={onSubmitClick(questions, beneficialOwnerQuestionKey)}
										questions={questions}
										allCountries={allCountries}
										initialValues={getQuestionnaireInitialValues(questions)}
									/>
								</Expandable>
							</Stack>
						</Card>
					);
				},
			)}
		</Stack>
	);
};

export default BeneficialOwnerQuestions;
